import { useState } from 'react';
import { useProlongedReconnecting, useRefreshHealth } from '@/hooks/useRefreshHealth';
import '@/components/ReconnectingPill.css';

export const RECONNECTING_PILL_TEXT = 'Reconectando — nada foi perdido';
export const RECONNECTING_CARD_TITLE = 'Ainda reconectando';
export const RECONNECTING_CARD_BODY =
  'O servidor está limitando conexões desta rede. Tudo que você fizer fica guardado — não precisa sair do app nem da conta.';
export const RECONNECTING_CARD_HINT = 'Toque para recolher';

/**
 * ReconnectingPill (JOSH-89, mockup "Reconectando" Option 3 · Pílula flutuante)
 *
 * A navy pill floating bottom-centre over the content while session health is
 * `reconnecting`; it never moves layout. After about two minutes of the same
 * reconnecting stretch it grows into a card that says work is kept and there
 * is no need to leave; a click collapses it back to the pill. On health `ok`
 * it disappears silently.
 *
 * Rendered in the shell's `.bottom-notice-stack` (App.tsx), above the
 * UpdateBanner, so the two never overlap.
 */
export default function ReconnectingPill() {
  const health = useRefreshHealth();
  const prolonged = useProlongedReconnecting(health);
  // The reconnecting stretch (its `since`) the user collapsed; a new stretch
  // starts expanded again once prolonged.
  const [collapsedFor, setCollapsedFor] = useState<number | null>(null);

  if (health.status !== 'reconnecting') return null;

  const since = health.since;
  const expanded = prolonged && collapsedFor !== since;

  return (
    <div className="reconnecting-notice" role="status" aria-live="polite">
      {expanded ? (
        <button
          type="button"
          className="reconnecting-card"
          onClick={() => setCollapsedFor(since)}
          aria-expanded="true"
        >
          <span className="reconnecting-card-title">
            <span className="reconnecting-spinner" aria-hidden="true" />
            {RECONNECTING_CARD_TITLE}
          </span>
          <span className="reconnecting-card-body">{RECONNECTING_CARD_BODY}</span>
          <span className="reconnecting-card-hint">{RECONNECTING_CARD_HINT}</span>
        </button>
      ) : (
        <div className="reconnecting-pill">
          <span className="reconnecting-spinner" aria-hidden="true" />
          {RECONNECTING_PILL_TEXT}
        </div>
      )}
    </div>
  );
}
